import { useTranslation } from 'react-i18next';
import { useCompanyFeatures } from '@/hooks/useCompanyFeatures';

/**
 * Single i18n indirection for the Team → Workspace display relabel (Phase 11).
 *
 * When `company.feature.workspaceIsolation` is ON, the account displays the word
 * "Workspace" wherever the UI would otherwise show "Team". This is a
 * **display-only** relabel — the `teams` entity, `teamId`, and every backend
 * path are unchanged. Spec: `03-workspaces-spec.md`.
 *
 * Usage — prefer this over branching at each string site:
 *  - Bare noun:   `const { noun, nounPlural } = useTeamNoun();` → render `{noun}`
 *  - Phrase:      `t('Add {{noun}}', { noun })` / `t('No {{noun}} found', { noun: nounPluralLower })`
 *  - Key pick:    `const { isWorkspace } = useTeamNoun();` → `t(isWorkspace ? 'Workspace Owner' : 'Team Owner')`
 *
 * The returned nouns are already translated, so pass them straight into
 * interpolation params. The keys `Team`/`Teams`/`team`/`teams` and their
 * `Workspace*` counterparts live in `public/i18n/en.json`.
 */
export function useTeamNoun() {
  const { t } = useTranslation();
  const { workspaceIsolation } = useCompanyFeatures();
  // The Workspace label is driven by the display-only `workspaceIsolation` relabel flag. Group
  // Security v3 no longer has its own account mode (the `userGroupSecurity` flag is gone — it is
  // additive on folder security), so the noun no longer keys off it. Display-only; backend unchanged.
  const isWorkspace = workspaceIsolation;

  return {
    /** true when this account displays the Workspace label instead of Team. */
    isWorkspace,
    /** Title-case singular: "Team" | "Workspace". */
    noun: isWorkspace ? t('Workspace') : t('Team'),
    /** Title-case plural: "Teams" | "Workspaces". */
    nounPlural: isWorkspace ? t('Workspaces') : t('Teams'),
    /** Lowercase singular for mid-sentence use: "team" | "workspace". */
    nounLower: isWorkspace ? t('workspace') : t('team'),
    /** Lowercase plural for mid-sentence use: "teams" | "workspaces". */
    nounPluralLower: isWorkspace ? t('workspaces') : t('teams'),
  };
}
